// Typed fetcher for studio.
//
// Two modes, selected by VITE_API_BASE_URL:
//   - set   → call the Hono API at `${VITE_API_BASE_URL}/v1/*` (dev, future deploys).
//   - unset → static mode. Fetch the snapshot JSON files copied into `public/snapshot/`
//             by scripts/copy-snapshot.mjs and project them client-side. Mirrors what
//             api/src/routes/* + api/src/lib/snapshot.ts do server-side. Same shapes.

import type {
  ApprovalRecord,
  ApprovalRequest,
  Artifact,
  Citation,
  CommentRecord,
  CommentRequest,
  Concept,
  ConceptSummary,
  Dag,
  Draft,
  GateReport,
  InboxItem,
  InboxResponse,
  Page,
  Snapshot,
  Source,
  TrustResponse,
} from '@disha/contracts';

const BASE = import.meta.env.VITE_API_BASE_URL;
const STATIC_MODE = !BASE;
const SNAPSHOT_PREFIX = '/snapshot';

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} (${path})`);
  return (await res.json()) as T;
}

async function networkGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new Error(`${res.status} ${body.code ?? ''}: ${body.error ?? res.statusText}`);
  }
  return (await res.json()) as T;
}

async function networkPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new Error(`${res.status} ${err.code ?? ''}: ${err.error ?? res.statusText}`);
  }
  return (await res.json()) as T;
}

function writesUnavailable(): never {
  throw new Error(
    'NOT_AVAILABLE: studio is in static mode (VITE_API_BASE_URL unset). Writes go through api/ — set it to enable Approve + Comment.',
  );
}

// Cached single-fetch helpers. Snapshot files are immutable per deploy.
let snapshotCache: Promise<Snapshot> | null = null;
let conceptsCache: Promise<Concept[]> | null = null;
let sourcesCache: Promise<Source[]> | null = null;
let artifactsCache: Promise<Artifact[]> | null = null;
let dagCache: Promise<Dag> | null = null;
let draftsCache: Promise<Draft[]> | null = null;
let gateReportCache: Promise<GateReport | null> | null = null;
const pagesCache = new Map<string, Promise<Map<number, Page>>>();

function loadSnapshotFile(): Promise<Snapshot> {
  if (!snapshotCache) snapshotCache = fetchJson<Snapshot>(`${SNAPSHOT_PREFIX}/snapshot.json`);
  return snapshotCache;
}
function loadConceptsFile(): Promise<Concept[]> {
  if (!conceptsCache) conceptsCache = fetchJson<Concept[]>(`${SNAPSHOT_PREFIX}/concepts.json`);
  return conceptsCache;
}
function loadSourcesFile(): Promise<Source[]> {
  if (!sourcesCache) sourcesCache = fetchJson<Source[]>(`${SNAPSHOT_PREFIX}/sources.json`);
  return sourcesCache;
}
function loadArtifactsFile(): Promise<Artifact[]> {
  if (!artifactsCache) artifactsCache = fetchJson<Artifact[]>(`${SNAPSHOT_PREFIX}/artifacts.json`);
  return artifactsCache;
}
function loadDagFile(): Promise<Dag> {
  if (!dagCache) dagCache = fetchJson<Dag>(`${SNAPSHOT_PREFIX}/dag.json`);
  return dagCache;
}
function loadDraftsFile(): Promise<Draft[]> {
  if (!draftsCache) draftsCache = fetchJson<Draft[]>(`${SNAPSHOT_PREFIX}/drafts.json`);
  return draftsCache;
}
function loadGateReportFile(): Promise<GateReport | null> {
  if (!gateReportCache) {
    gateReportCache = (async () => {
      const res = await fetch(`${SNAPSHOT_PREFIX}/gate-report.json`);
      if (!res.ok) return null;
      return (await res.json()) as GateReport;
    })();
  }
  return gateReportCache;
}
function loadPagesFile(sourceId: string): Promise<Map<number, Page>> {
  let p = pagesCache.get(sourceId);
  if (p) return p;
  p = (async () => {
    const res = await fetch(`${SNAPSHOT_PREFIX}/pages/${sourceId}.jsonl`);
    if (!res.ok) throw new Error(`NOT_FOUND: No pages indexed for source: ${sourceId}`);
    const text = await res.text();
    const out = new Map<number, Page>();
    for (const line of text.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const page = JSON.parse(trimmed) as Page;
      out.set(page.pageNumber, page);
    }
    return out;
  })();
  pagesCache.set(sourceId, p);
  return p;
}

const RECENT_LIMIT = 20;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function toInboxItem(d: Draft): InboxItem {
  return {
    draftId: d.id,
    kind: d.kind,
    conceptIds: d.conceptIds,
    bodyHash: d.bodyHash ?? '',
    approval: d.approval ?? null,
  };
}

// Mirror of api/src/routes/inbox.ts. Drives static-mode inbox so the
// surface still renders when VITE_API_BASE_URL is unset — the only thing
// missing is `openComments`, which lives in D1.
function deriveInbox(drafts: Draft[]): InboxResponse {
  const now = Date.now();
  const sevenDaysAgo = now - SEVEN_DAYS_MS;
  const stale: InboxItem[] = [];
  const pending: InboxItem[] = [];
  const approved: { item: InboxItem; approvedAtMs: number }[] = [];
  let approvedLast7d = 0;

  for (const d of drafts) {
    const item = toInboxItem(d);
    if (!d.approval) {
      pending.push(item);
      continue;
    }
    if (d.approval.bodyHash !== d.bodyHash) {
      stale.push(item);
      continue;
    }
    const approvedAtMs = Date.parse(d.approval.approvedAt);
    approved.push({ item, approvedAtMs });
    if (approvedAtMs >= sevenDaysAgo) approvedLast7d++;
  }
  approved.sort((a, b) => b.approvedAtMs - a.approvedAtMs);

  return {
    stats: {
      awaiting: pending.length,
      stale: stale.length,
      approvedLast7d,
      openComments: null,
    },
    stale,
    pending,
    recentlyApproved: approved.slice(0, RECENT_LIMIT).map((x) => x.item),
  };
}

function toConceptSummary(c: Concept): ConceptSummary {
  return {
    id: c.id,
    subject: c.subject,
    topic: c.topic,
    ...(c.subtopic ? { subtopic: c.subtopic } : {}),
    title: c.title,
    summary: c.summary,
    prereqs: c.prereqs,
    status: c.status,
  };
}

const staticApi = {
  health: async () => {
    const snap = await loadSnapshotFile();
    const concepts = await loadConceptsFile();
    return { ok: true, snapshot: snap.id, conceptCount: concepts.length };
  },
  snapshot: () => loadSnapshotFile(),
  sources: () => loadSourcesFile(),
  source: async (id: string): Promise<Source> => {
    const sources = await loadSourcesFile();
    const found = sources.find((s) => s.id === id);
    if (!found) throw new Error(`NOT_FOUND: Source not found: ${id}`);
    return found;
  },
  page: async (sourceId: string, pageNumber: number): Promise<Page> => {
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      throw new Error(`BAD_REQUEST: Invalid page number: ${pageNumber}`);
    }
    const pages = await loadPagesFile(sourceId);
    const page = pages.get(pageNumber);
    if (!page) throw new Error(`NOT_FOUND: Page ${pageNumber} not found for source: ${sourceId}`);
    return page;
  },
  concepts: async (): Promise<ConceptSummary[]> => {
    const concepts = await loadConceptsFile();
    return concepts.filter((c) => c.status === 'active').map(toConceptSummary);
  },
  concept: async (
    id: string,
  ): Promise<{
    concept: Concept;
    artifacts: Artifact[];
    citations: Citation[];
  }> => {
    const [concepts, artifacts] = await Promise.all([loadConceptsFile(), loadArtifactsFile()]);
    const concept = concepts.find((c) => c.id === id);
    if (!concept) throw new Error(`CONCEPT_NOT_FOUND: Concept not found: ${id}`);
    const attached = artifacts.filter((a) => a.conceptIds[0] === id);
    const seen = new Set<string>();
    const citations: Citation[] = [];
    for (const a of attached) {
      for (const c of a.citations) {
        const key = `${c.sourceId}#${c.pageNumber}`;
        if (seen.has(key)) continue;
        seen.add(key);
        citations.push(c);
      }
    }
    return { concept, artifacts: attached, citations };
  },
  artifacts: async (params: {
    kind?: string;
    conceptId?: string;
    purpose?: string;
    status?: string;
    canonical?: boolean;
  }): Promise<Artifact[]> => {
    const artifacts = await loadArtifactsFile();
    const status = params.status === 'all' ? null : (params.status ?? 'active');
    return artifacts.filter((a) => {
      if (params.kind && a.kind !== params.kind) return false;
      if (params.conceptId && a.conceptIds[0] !== params.conceptId) return false;
      if (params.purpose && a.purpose !== params.purpose) return false;
      if (status && a.status !== status) return false;
      if (params.canonical && !a.canonical) return false;
      return true;
    });
  },
  dag: () => loadDagFile(),
  trust: async (): Promise<TrustResponse> => {
    const [snap, gateReport, concepts, artifacts] = await Promise.all([
      loadSnapshotFile(),
      loadGateReportFile(),
      loadConceptsFile(),
      loadArtifactsFile(),
    ]);
    return {
      snapshotId: snap.id,
      createdAt: snap.createdAt,
      conceptCount: concepts.length,
      artifactCount: artifacts.length,
      gateReport,
    };
  },
  inbox: async (): Promise<InboxResponse> => {
    const drafts = await loadDraftsFile();
    return deriveInbox(drafts);
  },
  drafts: () => loadDraftsFile(),
  draft: async (id: string): Promise<Draft> => {
    const drafts = await loadDraftsFile();
    const found = drafts.find((d) => d.id === id);
    if (!found) throw new Error(`NOT_FOUND: Draft not found: ${id}`);
    return found;
  },
  // Writes have no static fallback — D1 lives behind the api/. The
  // surfaces that call these (`/review/:draftId`) are wired with mutation
  // states that render this error inline. Param lists mirror networkApi
  // so the unified `api` type stays callable from the caller's POV.
  comments: (_draftId: string): Promise<CommentRecord[]> => writesUnavailable(),
  approve: (_req: ApprovalRequest): Promise<ApprovalRecord> => writesUnavailable(),
  comment: (_req: CommentRequest): Promise<CommentRecord> => writesUnavailable(),
};

const networkApi = {
  health: () => networkGet<{ ok: boolean; snapshot: string; conceptCount: number }>('/health'),
  snapshot: () => networkGet<Snapshot>('/v1/snapshot'),
  sources: () => networkGet<Source[]>('/v1/sources'),
  source: (id: string) => networkGet<Source>(`/v1/sources/${id}`),
  page: (sourceId: string, pageNumber: number) =>
    networkGet<Page>(`/v1/sources/${sourceId}/pages/${pageNumber}`),
  concepts: () => networkGet<ConceptSummary[]>('/v1/concepts'),
  concept: (id: string) =>
    networkGet<{ concept: Concept; artifacts: Artifact[]; citations: Citation[] }>(
      `/v1/concepts/${id}`,
    ),
  artifacts: (params: {
    kind?: string;
    conceptId?: string;
    purpose?: string;
    status?: string;
    canonical?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params.kind) q.set('kind', params.kind);
    if (params.conceptId) q.set('conceptId', params.conceptId);
    if (params.purpose) q.set('purpose', params.purpose);
    if (params.status) q.set('status', params.status);
    if (params.canonical) q.set('canonical', '1');
    const qs = q.toString();
    return networkGet<Artifact[]>(`/v1/artifacts${qs ? `?${qs}` : ''}`);
  },
  dag: () => networkGet<Dag>('/v1/dag'),
  trust: () => networkGet<TrustResponse>('/v1/snapshot/trust'),
  inbox: () => networkGet<InboxResponse>('/v1/inbox'),
  // /v1/drafts isn't an api surface in v0 — drafts are operator-side state.
  // Studio's only need is the inbox + per-draft body, both served via the
  // inbox response shape (body lives in drafts.json today). When step 5
  // wires `/review/:draftId`, it reads from `loadDraftsFile()` directly
  // since the body is part of the bundled snapshot.
  drafts: () => loadDraftsFile(),
  draft: async (id: string): Promise<Draft> => {
    const drafts = await loadDraftsFile();
    const found = drafts.find((d) => d.id === id);
    if (!found) throw new Error(`NOT_FOUND: Draft not found: ${id}`);
    return found;
  },
  comments: (draftId: string) =>
    networkGet<CommentRecord[]>(`/v1/comments?draftId=${encodeURIComponent(draftId)}`),
  approve: (req: ApprovalRequest) => networkPost<ApprovalRecord>('/v1/approvals', req),
  comment: (req: CommentRequest) => networkPost<CommentRecord>('/v1/comments', req),
};

export const api = STATIC_MODE ? staticApi : networkApi;

// Surfaces that mutate (Approve, Comment) check this to render an
// inline notice and disable controls — keeps the failure mode visible
// instead of throwing on click.
export function isStaticMode(): boolean {
  return STATIC_MODE;
}
